import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@saas-boilerplate/shared';
import { toast } from 'sonner';
import { Toaster } from './ui/sonner';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Separator } from './ui/separator';
import { Avatar, AvatarFallback } from './ui/avatar';
import { invokeEdgeFn } from '@/lib/edge-functions';
import {
  Loader2,
  LogOut,
  LayoutGrid,
  List,
  MessageSquare,
  MessagesSquare,
  Pencil,
  ListChecks,
  Gavel,
  Users,
  Video,
  CheckCircle2,
  XCircle,
  Clock,
  FileText,
  ShieldCheck,
  Trash2,
  UserPlus,
} from 'lucide-react';
import { MeetingOccurrencePicker } from './meeting-occurrence-picker';
import { CheckInRecorder, CheckInList } from './check-in-recorder';
import { cn } from './ui/utils';
import appLogo from '@/assets/upstand_ai_logo.svg';

interface Props {
  seriesId: string;
  initialMeetingId: string | null;
  userId: string;
  seriesTitle: string;
  /** When true, show "All meetings" link in the header back to /meet portal. */
  showAllMeetingsLink?: boolean;
}

interface MeetingRow {
  id: string;
  starts_at: string | null;
  ends_at: string | null;
  status: string | null;
  video_meeting_url?: string | null;
}

type Tab = 'agenda' | 'checkins' | 'summary' | 'actions' | 'decisions' | 'people' | 'transcript' | 'chat' | 'recording';

type TranscriptSegment = { speaker?: string | null; text?: string | null };
type TranscriptRow = {
  transcript_text: string | null;
  transcript_segments: TranscriptSegment[] | null;
  short_summary: string | null;
  long_summary: string | null;
  status: string | null;
  transcript_status_label: string | null;
  bot_status_label: string | null;
};
type SpeakerResolutionRow = {
  diarisation_label: string;
  resolved_user_id: string | null;
  guest_label: string | null;
  resolved_display_name: string | null;
};
type RecordingState = {
  loading: boolean;
  status: 'ready' | 'processing' | 'no_recording' | 'unavailable' | 'error';
  video: { url: string; expires_at: string | null } | null;
  audio: { url: string; expires_at: string | null } | null;
  error: string | null;
};

export function AttendeeMeetingView({
  seriesId,
  initialMeetingId,
  userId,
  seriesTitle,
  showAllMeetingsLink = true,
}: Props) {
  const [meetings, setMeetings] = useState<MeetingRow[]>([]);
  const [focusedMeetingId, setFocusedMeetingId] = useState<string | null>(initialMeetingId);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>('agenda');

  const [agenda, setAgenda] = useState<any[]>([]);
  const [notes, setNotes] = useState('');
  const [decisions, setDecisions] = useState<any[]>([]);
  const [actions, setActions] = useState<any[]>([]);
  const [checkIns, setCheckIns] = useState<any[]>([]);
  const [attendees, setAttendees] = useState<any[]>([]);
  const [chatMessages, setChatMessages] = useState<any[]>([]);
  const [seriesDescription, setSeriesDescription] = useState<string | null>(null);
  const [workspaceName, setWorkspaceName] = useState<string | null>(null);


  // Transcript + recording - read-only for attendee/shared users. No admin affordances
  // (no Invite bot, Retry capture, etc) - just the content if it's there.
  const [transcript, setTranscript] = useState<TranscriptRow | null>(null);
  const [recordingState, setRecordingState] = useState<RecordingState | null>(null);
  /** workspace_id from the series - needed by the recording-url edge function. */
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);
  // Speaker attribution (read-only here): diarisation label → resolved name. Owners reassign
  // in the main meetings view; attendees/guests just see the resolved names.
  const [speakerResolutions, setSpeakerResolutions] = useState<SpeakerResolutionRow[]>([]);

  // ── Series header data ──
  useEffect(() => {
    (async () => {
      const { data: s } = await supabase
        .from('meeting_series')
        .select('id, description, workspace_id, workspaces(name)')
        .eq('id', seriesId)
        .maybeSingle();
      if (s) {
        setSeriesDescription(((s as any).description as string | null) || null);
        setWorkspaceName((((s as any).workspaces as any)?.name as string | null) || null);
        setWorkspaceId(((s as any).workspace_id as string | null) || null);
      }
    })();
  }, [seriesId]);

  // ── Sessions ──
  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from('meetings')
        .select('id, starts_at, ends_at, status, video_meeting_url')
        .eq('series_id', seriesId)
        .neq('status', 'cancelled')
        .order('starts_at', { ascending: false });
      const rows = (data ?? []) as MeetingRow[];
      setMeetings(rows);
      if (!focusedMeetingId && rows.length > 0) {
        setFocusedMeetingId(anchorMeetingId(rows));
      }
      setLoading(false);
    })();
  }, [seriesId, focusedMeetingId]);

  /** Anchor = next upcoming (starts >= now - 1h), else most recent past. Sessions are sorted DESC. */
  function anchorMeetingId(rows: MeetingRow[]): string | null {
    if (rows.length === 0) return null;
    const now = Date.now();
    // rows are DESC (newest first). Walk from newest backwards to find the closest future.
    const futureOrRecent = [...rows].reverse().find(
      (m) => m.starts_at && new Date(m.starts_at).getTime() >= now - 3600_000,
    );
    if (futureOrRecent) return futureOrRecent.id;
    return rows[0]?.id ?? null;
  }

  const focusedMeeting = useMemo(
    () => meetings.find((m) => m.id === focusedMeetingId) ?? meetings[0],
    [meetings, focusedMeetingId],
  );
  const focusedIndex = useMemo(
    () => meetings.findIndex((m) => m.id === focusedMeetingId),
    [meetings, focusedMeetingId],
  );

  // ── Per-meeting detail ──
  const loadDetail = useCallback(async () => {
    if (!focusedMeetingId) return;
    const mid = focusedMeetingId;
    const [{ data: g }, { data: n }, { data: d }, { data: a }, { data: c }, { data: att }, { data: tx }, { data: sr }, { data: chat }] = await Promise.all([
      supabase.from('agenda_items').select('*').eq('meeting_id', mid).order('sort_order'),
      supabase.from('meeting_notes').select('*').eq('meeting_id', mid).maybeSingle(),
      supabase.from('decisions').select('*').eq('meeting_id', mid).order('sort_order'),
      supabase.from('action_items').select('*').eq('meeting_id', mid).neq('status', 'dropped').order('created_at'),
      supabase
        .from('pre_meeting_check_ins')
        .select('id, meeting_id, user_id, body_text, content_type, media_path, media_mime, transcript_text, transcript_status, submitted_at')
        .eq('meeting_id', mid),
      supabase.from('meeting_attendees').select('*').eq('meeting_id', mid),
      supabase
        .from('meeting_transcripts')
        .select('transcript_text, transcript_segments, short_summary, long_summary, status, transcript_status_label, bot_status_label')
        .eq('meeting_id', mid)
        .maybeSingle(),
      supabase
        .from('meeting_speaker_resolutions')
        .select('diarisation_label, resolved_user_id, guest_label, resolved_display_name')
        .eq('meeting_id', mid),
      supabase.from('meeting_chat_messages').select('*').eq('meeting_id', mid).order('sent_at'),
    ]);
    setAgenda(g ?? []);
    setNotes(n?.content ?? '');
    setDecisions(d ?? []);
    setActions(a ?? []);
    setCheckIns(c ?? []);
    setAttendees(att ?? []);
    setTranscript((tx as TranscriptRow | null) ?? null);
    setSpeakerResolutions((sr as SpeakerResolutionRow[] | null) ?? []);
    setChatMessages(chat ?? []);
    // Reset recording - it's lazy-loaded when the Recording tab is opened (needs a signed
    // URL fetched from an edge function rather than being part of the row).
    setRecordingState(null);
  }, [focusedMeetingId, userId]);

  // Reload detail on meeting change AND on tab switch, so stale state (e.g. a transcript that recovered
  // server-side) refreshes without a full page reload. loadDetail is a silent in-place refetch (no spinner).
  useEffect(() => {
    void loadDetail();
  }, [loadDetail, tab]);

  /**
   * Lazy-fetch the signed recording URL when the Recording tab opens. We don't refetch
   * unconditionally if a successful state is already cached (URLs are short-lived but the user
   * can hit refresh if they expire).
   */
  const loadRecording = useCallback(async () => {
    if (!focusedMeetingId || !workspaceId) return;
    setRecordingState({ loading: true, status: 'unavailable', video: null, audio: null, error: null });
    try {
      const res = await invokeEdgeFn<{
        status: RecordingState['status'];
        video?: { url: string; expires_at: string | null } | null;
        audio?: { url: string; expires_at: string | null } | null;
        error?: string;
      }>('recording-url', { meeting_id: focusedMeetingId, workspace_id: workspaceId });
      setRecordingState({
        loading: false,
        status: res.status || 'unavailable',
        video: res.video || null,
        audio: res.audio || null,
        error: res.error || null,
      });
    } catch (err: any) {
      setRecordingState({
        loading: false,
        status: 'error',
        video: null,
        audio: null,
        error: err?.message || 'Failed to load recording.',
      });
    }
  }, [focusedMeetingId, workspaceId]);

  useEffect(() => {
    if (tab !== 'recording') return;
    if (recordingState && !recordingState.error) return;
    void loadRecording();
  }, [tab, recordingState, loadRecording]);

  const signOut = async () => {
    await supabase.auth.signOut();
    window.location.href = '/';
  };

  // Read-only speaker attribution: map each diarisation label to its resolved name. Resolved
  // names are denormalised onto the resolution row (so shared guests, who can't read profiles,
  // still see them). Any unresolved "Speaker X" gets a stable "Guest N" in first-appearance order.
  const speakerDisplayFor = useMemo(() => {
    const byLabel = new Map<string, SpeakerResolutionRow>();
    for (const r of speakerResolutions) {
      if (r.diarisation_label) byLabel.set(r.diarisation_label, r);
    }
    const guestNumber = new Map<string, number>();
    let nextGuest = 1;
    const segs = Array.isArray(transcript?.transcript_segments) ? transcript!.transcript_segments! : [];
    for (const seg of segs) {
      const label = (seg.speaker || '').trim();
      if (!label) continue;
      const res = byLabel.get(label);
      const resolvedName = (res?.resolved_display_name || res?.guest_label || '').trim();
      if (!resolvedName && /^speaker\b/i.test(label) && !guestNumber.has(label)) {
        guestNumber.set(label, nextGuest++);
      }
    }
    return (rawLabel: string | null | undefined): string => {
      const label = (rawLabel || '').trim();
      if (!label) return '';
      const res = byLabel.get(label);
      const resolvedName = (res?.resolved_display_name || res?.guest_label || '').trim();
      if (resolvedName) return resolvedName;
      const n = guestNumber.get(label);
      if (n != null) return `Guest ${n}`;
      return label;
    };
  }, [speakerResolutions, transcript]);

  // ── Helpers ──
  const attendeeLabel = (att: any): string => {
    const name = (att?.name || att?.display_name || att?.full_name || '').trim();
    if (name) return name;
    const em = (att?.email || '').trim();
    if (em) {
      const local = em.split('@')[0] || '';
      return local.replace(/[._-]+/g, ' ').replace(/\s+/g, ' ').trim() || em;
    }
    return 'Invitee';
  };
  const attendeeInitial = (att: any): string => (attendeeLabel(att).charAt(0) || '?').toUpperCase();
  const actionAssigneeLabel = (action: any): string => {
    if (action.assignee_name) return action.assignee_name;
    if (action.assignee_email) return action.assignee_email;
    if (action.assignee_user_id) {
      const att = attendees.find((a: any) => a.user_id === action.assignee_user_id);
      if (att) return attendeeLabel(att);
    }
    return 'Unassigned';
  };
  const checkInAuthorLabel = (submitterId: string): string => {
    if (submitterId === userId) return 'You';
    const att = attendees.find((a: any) => a.user_id === submitterId);
    return att ? attendeeLabel(att) : 'Attendee';
  };
  const formatTimeRange = (start: string | null | undefined, end: string | null | undefined): string => {
    if (!start) return '';
    const s = new Date(start);
    const startStr = s.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    if (!end) return startStr;
    const e = new Date(end);
    const endStr = e.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    return `${startStr} – ${endStr}`;
  };
  const formatDateLong = (iso: string | null | undefined): string => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const ownCheckIn = checkIns.find((x: any) => x.user_id === userId);
  const otherCheckIns = checkIns.filter((x: any) => x.user_id !== userId);
  const hasAgenda = agenda.length > 0;
  const hasNotes = Boolean((notes || '').trim());

  // ── Loading ──
  // ── Guest voiceprint consent (account-less attendee invited by email; see invite-meeting-guest) ──
  // Driven by ?voiceconsent=<meetingId> on the magic-link landing. We match the signed-in email to the
  // guest_email the owner recorded on that meeting's resolution, then offer consent → voiceprint here
  // (the guest can't reach Settings → Voice). Deletable for GDPR symmetry. MUST stay above the early
  // returns below - hooks can't run conditionally (React #310).
  const [consentOffer, setConsentOffer] = useState<{ meetingId: string; label: string; name: string } | null>(null);
  const [guestHasVoiceprint, setGuestHasVoiceprint] = useState(false);
  const [voiceParam, setVoiceParam] = useState(false);
  const [consentBusy, setConsentBusy] = useState(false);

  useEffect(() => {
    const mid = new URLSearchParams(window.location.search).get('voiceconsent');
    if (!mid) return;
    setVoiceParam(true);
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      const email = (auth?.user?.email || '').trim().toLowerCase();
      if (!email) return;
      const [{ data: res }, { data: vp }] = await Promise.all([
        supabase
          .from('meeting_speaker_resolutions')
          .select('diarisation_label, guest_label, guest_email, resolved_user_id')
          .eq('meeting_id', mid),
        supabase.from('voiceprints').select('id').eq('user_id', userId).is('deleted_at', null).limit(1),
      ]);
      setGuestHasVoiceprint((((vp as any[]) ?? []).length > 0));
      const match = (((res as any[]) ?? [])).find(
        (r) => (r.guest_email || '').trim().toLowerCase() === email && !r.resolved_user_id,
      );
      if (match) setConsentOffer({ meetingId: mid, label: match.diarisation_label as string, name: (match.guest_label as string) || 'you' });
    })();
  }, [userId]);

  const respondConsent = useCallback(
    async (decision: 'accept' | 'decline') => {
      if (!consentOffer) return;
      setConsentBusy(true);
      try {
        const res = await invokeEdgeFn<{ built?: boolean }>('consent-guest-voiceprint', {
          meeting_id: consentOffer.meetingId,
          diarisation_label: consentOffer.label,
          decision,
        });
        if (decision === 'decline') {
          toast.success('No problem - dismissed.');
        } else if (res?.built) {
          setGuestHasVoiceprint(true);
          toast.success("Thanks - your voiceprint is set. We'll recognise you automatically next time.");
        } else {
          toast.success("Thanks for consenting - we couldn't get clean audio from this meeting, but your consent is recorded.");
        }
        setConsentOffer(null);
      } catch (e: any) {
        toast.error(e?.message || 'Could not save your choice');
      } finally {
        setConsentBusy(false);
      }
    },
    [consentOffer],
  );

  const deleteMyVoiceprint = useCallback(async () => {
    if (!workspaceId) return;
    setConsentBusy(true);
    try {
      await invokeEdgeFn('delete-voiceprint', { workspace_id: workspaceId });
      setGuestHasVoiceprint(false);
      toast.success('Voiceprint deleted.');
    } catch (e: any) {
      toast.error(e?.message || 'Could not delete your voiceprint');
    } finally {
      setConsentBusy(false);
    }
  }, [workspaceId]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!focusedMeeting) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <AttendeeViewHeader
          workspaceName={workspaceName}
          showAllMeetingsLink={showAllMeetingsLink}
          onSignOut={signOut}
        />
        <main className="mx-auto w-full max-w-4xl px-4 py-12 sm:px-6">
          <p className="text-sm text-muted-foreground">No meetings yet in this series.</p>
        </main>
        <Toaster />
      </div>
    );
  }

  // ── Render ──

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <AttendeeViewHeader
        workspaceName={workspaceName}
        showAllMeetingsLink={showAllMeetingsLink}
        onSignOut={signOut}
      />

      <main className="mx-auto w-full max-w-4xl flex-1 px-4 pb-12 pt-4 sm:px-6">
        {voiceParam && guestHasVoiceprint ? (
          <div className="mb-4 flex items-start gap-3 rounded-lg border border-primary/40 bg-primary/5 p-4">
            <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
            <div className="flex-1">
              <p className="text-sm font-medium">Your voiceprint is set</p>
              <p className="text-sm text-muted-foreground">
                upstand.ai will recognise your voice automatically in future meetings. Stored as a numeric
                signature (not a recording) - you can delete it anytime.
              </p>
              <Button
                variant="ghost"
                size="sm"
                className="mt-2 text-destructive hover:text-destructive"
                disabled={consentBusy}
                onClick={() => void deleteMyVoiceprint()}
              >
                <Trash2 className="mr-2 h-4 w-4" /> Delete my voiceprint
              </Button>
            </div>
          </div>
        ) : consentOffer ? (
          <div className="mb-4 flex items-start gap-3 rounded-lg border border-primary/40 bg-primary/5 p-4">
            <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
            <div className="flex-1">
              <p className="text-sm font-medium">Confirm your voice</p>
              <p className="text-sm text-muted-foreground">
                You were identified as <span className="font-medium text-foreground">{consentOffer.name}</span> in
                this meeting. Build a voiceprint so upstand.ai recognises you automatically next time? It&apos;s
                stored as a numeric signature (not a recording), recognised as biometric data under GDPR/BIPA, and
                deletable anytime.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button size="sm" disabled={consentBusy} onClick={() => void respondConsent('accept')}>
                  {consentBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
                  Consent &amp; build
                </Button>
                <Button size="sm" variant="ghost" disabled={consentBusy} onClick={() => void respondConsent('decline')}>
                  Not now
                </Button>
              </div>
            </div>
          </div>
        ) : null}

        {/* Title + description + Join button */}
        <header className="mb-4 flex flex-col justify-between gap-4 md:flex-row md:items-start">
          <div className="space-y-1">
            <h1 className="font-display text-xl font-semibold tracking-tight text-foreground md:text-2xl">
              {seriesTitle}
            </h1>
            <p className="max-w-2xl text-sm leading-snug text-muted-foreground">
              {seriesDescription ? (
                <span className="line-clamp-2">{seriesDescription}</span>
              ) : (
                <span className="italic text-muted-foreground/90">No description yet.</span>
              )}
            </p>
          </div>
          {focusedMeeting.video_meeting_url && (
            <Button
              type="button"
              variant="outline"
              className="border-primary/40 bg-primary/10 text-primary hover:bg-primary/15"
              onClick={() => window.open(focusedMeeting.video_meeting_url!, '_blank', 'noopener,noreferrer')}
            >
              <Video className="mr-1.5 h-4 w-4" />
              Join video call
            </Button>
          )}
        </header>

        {/* Meeting picker */}
        {meetings.length > 0 && (
          <MeetingOccurrencePicker
            sessions={meetings}
            focusedIndex={focusedIndex}
            onSelect={(id) => setFocusedMeetingId(id)}
          />
        )}

        {/* Meeting time line */}
        <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
          <span className="font-medium text-foreground">
            {formatDateLong(focusedMeeting.starts_at)}
          </span>
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" />
            {formatTimeRange(focusedMeeting.starts_at, focusedMeeting.ends_at)}
          </span>
        </div>

        {/* Tab nav */}
        <div className="mb-6 mt-4 flex flex-wrap gap-x-6 gap-y-2 border-b border-outline-variant/10">
          {(
            [
              ['agenda', 'Agenda', List, hasAgenda ? CheckCircle2 : XCircle, hasAgenda ? 'text-emerald-600' : 'text-muted-foreground'],
              ['checkins', 'Check-ins', MessageSquare, null, ''],
              ['summary', 'Notes', Pencil, hasNotes ? CheckCircle2 : XCircle, hasNotes ? 'text-emerald-600' : 'text-muted-foreground'],
              ['actions', 'Action items', ListChecks, null, ''],
              ['decisions', 'Decisions', Gavel, null, ''],
              ['people', 'Attendees', Users, null, ''],
              ['transcript', 'Transcript', FileText, null, ''],
              ['chat', 'Chat', MessagesSquare, null, ''],
              ['recording', 'Recording', Video, null, ''],
            ] as const
          ).map(([id, label, Icon, StatusIcon, statusIconClass]) => {
            const active = tab === (id as Tab);
            const count = id === 'checkins'
              ? checkIns.length
              : id === 'actions'
                ? actions.length
                : id === 'decisions'
                  ? decisions.length
                  : id === 'people'
                    ? attendees.length
                    : id === 'chat'
                      ? chatMessages.length
                      : null;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setTab(id as Tab)}
                className={cn(
                  'pb-3 text-sm font-medium transition-colors',
                  active
                    ? 'border-b-2 border-primary font-semibold text-primary'
                    : 'text-muted-foreground hover:text-primary',
                )}
              >
                <span className="inline-flex items-center gap-1.5">
                  <Icon className="h-3.5 w-3.5" strokeWidth={1.8} />
                  <span>{label}</span>
                  {count != null ? <span>({count})</span> : null}
                  {StatusIcon ? <StatusIcon className={cn('h-3.5 w-3.5', statusIconClass)} strokeWidth={1.8} /> : null}
                </span>
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        <div className="space-y-6">
          {tab === 'agenda' && (
            <section className="space-y-3">
              <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Agenda
              </Label>
              {agenda.length === 0 ? (
                <p className="text-sm italic text-muted-foreground">No agenda items yet.</p>
              ) : (
                <ul className="space-y-2">
                  {agenda.map((item: any) => (
                    <li
                      key={item.id}
                      className="rounded-md border border-border bg-card px-4 py-3 text-sm"
                    >
                      <div className="flex flex-wrap items-baseline justify-between gap-3">
                        <span className="font-medium text-foreground">{item.title}</span>
                        {item.time_minutes ? (
                          <span className="text-xs text-muted-foreground">{item.time_minutes} min</span>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          {tab === 'checkins' && (
            <section className="space-y-4">
              <div className="space-y-2 rounded-md border border-border bg-card p-4">
                <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  Add an update
                </Label>
                {focusedMeetingId && (
                  <CheckInRecorder meetingId={focusedMeetingId} userId={userId} onSubmitted={loadDetail} />
                )}
              </div>

              <div className="space-y-3 rounded-md border border-border bg-card p-4">
                <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  Submissions
                </Label>
                <CheckInList
                  items={checkIns}
                  authorLabel={checkInAuthorLabel}
                  emptyText="No updates yet."
                  currentUserId={userId}
                  onChanged={loadDetail}
                />
              </div>
            </section>
          )}

          {tab === 'chat' && (
            <section className="space-y-3">
              <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                In-meeting chat
              </Label>
              {chatMessages.length === 0 ? (
                <p className="text-sm text-muted-foreground">No in-meeting chat was captured for this meeting.</p>
              ) : (
                <ul className="space-y-2">
                  {chatMessages.map((m) => (
                    <li key={m.id} className="rounded-lg border border-border/60 bg-card p-3">
                      <div className="mb-0.5 flex items-baseline justify-between gap-2">
                        <span className="text-sm font-medium text-foreground">{m.sender_name || 'Unknown'}</span>
                        {m.sent_at ? (
                          <span className="shrink-0 text-xs text-muted-foreground">
                            {new Date(m.sent_at).toLocaleString(undefined, {
                              day: 'numeric',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </span>
                        ) : null}
                      </div>
                      <p className="whitespace-pre-wrap text-sm text-muted-foreground">{m.body}</p>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          {tab === 'summary' && (
            <section className="space-y-3">
              <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Notes
              </Label>
              {notes ? (
                <div className="whitespace-pre-wrap rounded-md border border-border bg-card p-4 text-sm leading-relaxed text-foreground/90">
                  {notes}
                </div>
              ) : (
                <p className="text-sm italic text-muted-foreground">No notes yet.</p>
              )}
            </section>
          )}

          {tab === 'actions' && (
            <section className="space-y-3">
              <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Action items
              </Label>
              {actions.length === 0 ? (
                <p className="text-sm italic text-muted-foreground">No action items yet.</p>
              ) : (
                <ul className="space-y-2">
                  {actions.map((a: any) => {
                    const status = String(a.status || 'open').toLowerCase();
                    const badgeCls =
                      status === 'done'
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : status === 'in_progress'
                          ? 'bg-blue-50 text-blue-700 border-blue-200'
                          : status === 'dropped'
                            ? 'bg-orange-50 text-orange-700 border-orange-200'
                            : 'bg-muted text-muted-foreground border-border';
                    const badgeLabel =
                      status === 'done'
                        ? 'Done'
                        : status === 'in_progress'
                          ? 'In progress'
                          : status === 'dropped'
                            ? 'Archived'
                            : 'Open';
                    return (
                      <li key={a.id} className="rounded-md border border-border bg-card px-4 py-3 text-sm">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <p className="min-w-0 flex-1 font-medium text-foreground">{a.title}</p>
                          <div className="flex shrink-0 flex-wrap items-center gap-2">
                            <span className="text-xs text-muted-foreground">{actionAssigneeLabel(a)}</span>
                            <span className={cn('rounded-full border px-2 py-0.5 text-[11px] font-medium', badgeCls)}>
                              {badgeLabel}
                            </span>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          )}

          {tab === 'decisions' && (
            <section className="space-y-3">
              <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Decisions
              </Label>
              {decisions.length === 0 ? (
                <p className="text-sm italic text-muted-foreground">No decisions recorded.</p>
              ) : (
                <ul className="space-y-2">
                  {decisions.map((d: any) => (
                    <li key={d.id} className="rounded-md border border-border bg-card px-4 py-3 text-sm">
                      <p className="text-foreground/90">{d.body || d.title}</p>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          {tab === 'transcript' && (
            <section className="space-y-4">
              <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Transcript
              </Label>
              {Array.isArray(transcript?.transcript_segments) && transcript!.transcript_segments!.length > 0 ? (
                <div className="space-y-2 rounded-md border border-border bg-surface-container-low/40 p-4 text-sm">
                  {transcript!.transcript_segments!.map((seg, i) => (
                    <div key={i} className="flex gap-2.5">
                      {seg.speaker ? (
                        <span className="w-28 shrink-0 truncate font-medium text-foreground/80">
                          {speakerDisplayFor(seg.speaker)}
                        </span>
                      ) : null}
                      <span className={cn('leading-relaxed text-foreground/90', !seg.speaker ? 'w-full' : 'min-w-0 flex-1')}>
                        {seg.text}
                      </span>
                    </div>
                  ))}
                </div>
              ) : transcript?.transcript_text ? (
                <Textarea rows={12} readOnly value={transcript.transcript_text} />
              ) : (
                <p className="text-sm italic text-muted-foreground">
                  {transcript?.transcript_status_label || transcript?.bot_status_label || 'Transcript not available yet.'}
                </p>
              )}
              {transcript?.short_summary ? (
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                    Summary
                  </Label>
                  <p className="rounded-md border border-border bg-card px-3 py-3 text-sm leading-relaxed text-foreground/90">
                    {transcript.short_summary}
                  </p>
                </div>
              ) : null}
            </section>
          )}

          {tab === 'recording' && (
            <section className="space-y-4">
              <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Recording
              </Label>
              {!recordingState || recordingState.loading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading recording…
                </div>
              ) : recordingState.status === 'no_recording' ? (
                <p className="text-sm italic text-muted-foreground">No recording was captured for this meeting.</p>
              ) : recordingState.status === 'processing' ? (
                <p className="text-sm italic text-muted-foreground">
                  Recording is still being processed. Check back shortly.
                </p>
              ) : recordingState.status === 'error' ? (
                <p className="text-sm text-destructive">{recordingState.error || 'Failed to load recording.'}</p>
              ) : recordingState.video?.url ? (
                <div className="space-y-3">
                  <video
                    src={recordingState.video.url}
                    controls
                    className="w-full rounded-md border border-border bg-black"
                  />
                  {recordingState.audio?.url && (
                    <div className="space-y-1">
                      <Label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Audio only
                      </Label>
                      <audio src={recordingState.audio.url} controls className="w-full" />
                    </div>
                  )}
                </div>
              ) : recordingState.audio?.url ? (
                <audio src={recordingState.audio.url} controls className="w-full" />
              ) : (
                <p className="text-sm italic text-muted-foreground">Recording unavailable.</p>
              )}
            </section>
          )}

          {tab === 'people' && (
            <section className="space-y-3">
              <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Attendees
              </Label>
              {attendees.length === 0 ? (
                <p className="text-sm italic text-muted-foreground">No invitees yet.</p>
              ) : (
                <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {attendees.map((att: any) => {
                    const rsvp = String(att.rsvp || '').toLowerCase();
                    const rsvpCls =
                      rsvp === 'accepted'
                        ? 'text-emerald-700'
                        : rsvp === 'declined'
                          ? 'text-rose-700'
                          : rsvp === 'tentative'
                            ? 'text-amber-700'
                            : 'text-muted-foreground';
                    const rsvpLabel =
                      rsvp === 'accepted'
                        ? 'Accepted'
                        : rsvp === 'declined'
                          ? 'Declined'
                          : rsvp === 'tentative'
                            ? 'Tentative'
                            : 'No response';
                    return (
                      <li
                        key={att.id || att.user_id || att.email}
                        className="flex items-center gap-3 rounded-md border border-border bg-card px-3 py-2.5 text-sm"
                      >
                        <Avatar className="h-8 w-8 shrink-0">
                          <AvatarFallback className="bg-primary/10 text-xs font-semibold text-primary">
                            {attendeeInitial(att)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-medium text-foreground">{attendeeLabel(att)}</div>
                          <div className={cn('text-xs', rsvpCls)}>
                            {att.is_organizer ? 'Organiser · ' : ''}
                            {rsvpLabel}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          )}
        </div>
      </main>

      <Toaster />
    </div>
  );
}

function AttendeeViewHeader({
  workspaceName,
  showAllMeetingsLink,
  onSignOut,
}: {
  workspaceName: string | null;
  showAllMeetingsLink: boolean;
  onSignOut: () => void;
}) {
  return (
    <header className="sticky top-0 z-50 flex items-center justify-between gap-3 border-b border-border/40 bg-background/80 px-4 py-2 backdrop-blur-xl sm:px-6">
      <div className="flex min-w-0 items-center gap-3">
        <img src={appLogo} alt="Upstand.ai" className="h-7 w-auto sm:h-8" />
        {workspaceName ? (
          <>
            <span className="hidden text-muted-foreground/60 sm:inline">/</span>
            <span className="truncate text-sm font-medium text-foreground">{workspaceName}</span>
          </>
        ) : null}
      </div>
      <div className="flex items-center gap-2">
        {showAllMeetingsLink && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-muted-foreground"
            onClick={() => {
              window.location.href = '/meet';
            }}
          >
            <LayoutGrid className="mr-1 h-4 w-4" />
            All meetings
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            // Drop out of the attendee gate into the normal app; with no membership they land on
            // workspace creation (their own account). Their attendee access here is unaffected.
            window.location.href = '/';
          }}
        >
          <UserPlus className="mr-1 h-4 w-4" />
          Create your account
        </Button>
        <Button type="button" variant="ghost" size="sm" className="text-muted-foreground" onClick={onSignOut}>
          <LogOut className="mr-1 h-4 w-4" />
          Sign out
        </Button>
      </div>
    </header>
  );
}
